<template>
    <div class="appointment-event-goal-modal">
        <DsModal
            v-if="areAppointmentTypesEmpty && !isLoading"
            class="set-up-modal"
            :title="$t('funnel.edit.sideBar.goal.appointmentEvent.setup.title')"
            :is-open="isOpen"
            show-close
            @close="closeModal"
        >
            <template #default>
                <div class="set-up-appointment-body">
                    <DsIllustration
                        name="calendar-color"
                        class="calendar-image"
                    />

                    <p class="setup-title">
                        {{ $t('funnel.edit.sideBar.goal.appointmentEvent.setup.book') }}
                    </p>

                    <p class="setup-message">
                        {{ $t('funnel.edit.sideBar.goal.appointmentEvent.setup.overview') }}
                    </p>

                    <DsLink
                        class="setup-link"
                        data-qa="redirect-to-appointments-button"
                        :href="appointmentsUrl"
                    >
                        {{ $t('funnel.edit.sideBar.goal.appointmentEvent.setup.title') }}
                    </DsLink>
                </div>
            </template>
        </DsModal>

        <GoalModal
            v-else-if="!isLoading"
            class="appointment-event-modal"
            :is-open="isOpen"
            :goal-tip-link="$t('funnel.edit.sideBar.goal.appointmentEvent.tooltip.url')"
            :goal-tip-text="$t('funnel.edit.sideBar.goal.appointmentEvent.tooltip.overview')"
            :summary-text="$t('funnel.edit.sideBar.goal.appointmentEvent.summary')"
            :title="$t('funnel.edit.sideBar.goal.appointmentEvent.title')"
            @update-config="updateConfig"
            @close-modal="closeModal"
        >
            <template #goalParameters>
                <div class="vertical-container">
                    <span class="span-label">
                        {{ $t('funnel.edit.sideBar.goal.appointmentEvent.when.contact.label') }}
                    </span>
                    <DsMultiselect
                        v-model="selectedSchedule"
                        bind-value-only
                        label-prop="name"
                        value-prop="id"
                        class="appointment-schedule-select"
                        :placeholder="$t('funnel.edit.sideBar.goal.appointmentEvent.when.contact.placeholder')"
                        :options="schedulesOptions"
                    />
                    <span class="span-label">
                        {{ $t('funnel.edit.sideBar.goal.appointmentEvent.an.label') }}
                    </span>
                    <DsMultiselect
                        v-model="selectedAppointmentType"
                        bind-value-only
                        label-prop="name"
                        value-prop="id"
                        required
                        help-text
                        help-text-prop="durationMinutes"
                        class="appointment-types-select"
                        :placeholder="$t('funnel.edit.sideBar.goal.appointmentEvent.select.appointment.type.placeholder')"
                        :options="appointmentTypesOptions"
                        :force-invalid="showError"
                    >
                        <template #help>
                            <span v-if="showError">
                                {{ $t('funnel.edit.sideBar.goal.appointmentEvent.error.required') }}
                            </span>
                        </template>
                    </DsMultiselect>
                </div>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import { getAppointmentTypes } from './appointment.event.api';
import GoalModal from '../goalModal/GoalModal.vue';

export const APPOINTMENT_EVENT_STATUS = {
    CREATED: 'CREATED',
    UPDATED: 'UPDATED',
    DELETED: 'DELETED',
};

export default {
    name: 'AppointmentEvent',
    components: { GoalModal },
    APPOINTMENT_EVENT_STATUS,
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    appointmentEventStatus: null,
                    appointmentTypeIds: [],
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            selectedSchedule: null,
            schedulesOptions: [],
            selectedAppointmentType: null,
            appointmentTypesOptions: [],
            areAppointmentTypesEmpty: true,
            isLoading: false,
            showError: false,
        };
    },
    computed: {
        appointmentsUrl() {
            const spaBaseUrl = this.appInfo?.spaBaseUrl;
            const appointmentUrl = `${spaBaseUrl}/appointments/new`;

            return appointmentUrl;
        },
        appName() {
            return this.appInfo?.appName;
        },
    },
    async beforeMount() {
        try {
            this.isLoading = true;
            const appointmentTypes = await getAppointmentTypes();

            this.appointmentTypesOptions = appointmentTypes.map((appt) => ({
                id: appt.id,
                name: `${appt.title}`,
                icon: 'calendar',
                durationMinutes: this.durationLabel(appt.durationMinutes),
            }));

            if (this.appointmentTypesOptions.length > 0) {
                this.areAppointmentTypesEmpty = false;
            }

            this.setFormValues();
        } catch (error) {
            const errorMessage = this.$t('funnel.edit.sideBar.goal.appointmentEvent.error.message');

            this.$error({
                message: errorMessage,
            });
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        updateConfig() {
            this.showError = this.selectedAppointmentType === null;

            if (!this.showError) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};

                cell.value.appointmentEventStatus = this.selectedSchedule;
                cell.value.appointmentTypeIds = [this.selectedAppointmentType];

                this.$emit('update-cell-value', { cell });
            }
        },
        closeModal() {
            this.$emit('close-modal');
        },
        setFormValues() {
            const schedules = this.$t('funnel.edit.sideBar.goal.appointmentEvent.schedules');
            const reschedules = this.$t('funnel.edit.sideBar.goal.appointmentEvent.reschedules');
            const cancels = this.$t('funnel.edit.sideBar.goal.appointmentEvent.cancels');


            this.schedulesOptions = [
                {
                    name: schedules,
                    id: this.$options.APPOINTMENT_EVENT_STATUS.CREATED,
                }, {
                    name: reschedules,
                    id: this.$options.APPOINTMENT_EVENT_STATUS.UPDATED,
                }, {
                    name: cancels,
                    id: this.$options.APPOINTMENT_EVENT_STATUS.DELETED,
                },
            ];

            const indexScheduleSelected = this.cell?.value?.appointmentEventStatus;
            const indexTypesSelected = this.cell?.value?.appointmentTypeIds;

            if (indexScheduleSelected) {
                this.selectedSchedule = indexScheduleSelected;
            }

            if (indexTypesSelected?.length > 0) {
                const [firstAppointmentType] = indexTypesSelected;

                if (this.appointmentTypesOptions.some((appt) => appt.id === firstAppointmentType)) {
                    this.selectedAppointmentType = firstAppointmentType;
                } else {
                    this.selectedAppointmentType = null;
                }
            }
        },
        durationLabel(minutes) {
            if (minutes <= 60) {
                return `${minutes} minutes`;
            }
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;

            if (remainingMinutes === 0) {
                return `${hours} hours`;
            }

            return `${hours} hours and ${remainingMinutes} minutes`;
        },
    },
};
</script>

<style lang="scss" scoped>
.span-label {
    font-weight: bold;
    margin-bottom: $spacing-100;
}

.set-up-appointment-body {
    align-self: center;
    text-align: center;
    padding: 0 $spacing-200 $spacing-200;
    display: flex;
    flex-direction: column;
}

.setup-title {
    font-weight: 400;
    font-size: px-to-rem(18);
    line-height: $spacing-300;
    height: $spacing-300;
    width: px-to-rem(552);
}

.setup-message {
    align-self: center;
    text-align: center;
    margin-bottom: $spacing-200;
}

.calendar-image {
    align-self: center;
    height: px-to-rem(72);
    width: px-to-rem(72);
}

.setup-link {
    display: block;
    font-weight: 700;
    font-size: px-to-rem(14);
    line-height: $spacing-200;
    padding: $spacing-150 $spacing-200 $spacing-150 $spacing-200;
    color: $color-blue-700;
}

</style>

<style lang="scss">
.appointment-event-goal-modal {
    div.modal-body,
    div.multiselect-tags > span > label {
        padding-top: 0;
    }
}
</style>
